import { useEffect, useState, KeyboardEvent } from "react";import { Snackbar } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";
import MUIDataTable, { MUIDataTableColumn, MUISortOptions, SelectableRows } from "mui-datatables";
import Http from "../engine/Http";
import ButtonsPanel from "./ButtonsPanel";
import { DetailsProps } from "./DetailsDialog";
import { CircularProgress } from "@material-ui/core";
import { ClassNameMap } from "@material-ui/styles";
import { useKeycloak } from "@react-keycloak/web";

interface Props {
  title: string;
  resource: string;
  columns: MUIDataTableColumn[];
  selectableRows?: SelectableRows;
  tableSearchEnabled?: boolean;
  createEnabled?: boolean;
  deleteEnabled?: boolean;
  deleteRequiresConfirmation?: boolean
  defaultSortOrder?: MUISortOptions;
  lastViewRefresh?: Date;
  renderDetails: (detailsProps: DetailsProps, itemData?: any) => JSX.Element;
  convert?: (item: any) => any;
  afterSave?: (data: any, closeDialog: boolean) => void;
  renderCustomButtons?: (
    classes: ClassNameMap,
    getSelectedIds: () => number[],
    fetchData: () => void,
    reportAlerts: (messages: string[]) => void
  ) => JSX.Element;
  sortData?: (data: any[], columnIndex: number, order: string) => any[];
  onDelete?: (resourceId: number | null) => Promise<any>;
}

const DataPanel = (props: Props) => {
  const { keycloak } = useKeycloak();
  const [data, setData] = useState<any[]>();
  const [displayDetails, setDisplayDetails] = useState(false);
  const [displayedId, setDisplayedId] = useState<number|null>(null);
  const [displayedItem, setDisplayedItem] = useState<any>();
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
  const exactlyOneRowSelected = selectedIndexes.length === 1;
  const editEnabled = exactlyOneRowSelected;
  const isCreateEnabled = (props.createEnabled === undefined || props.createEnabled === true) 
  const deleteEnabled = (props.deleteEnabled === undefined || props.deleteEnabled === true) && exactlyOneRowSelected;
  const [alertMessages, setAlertMessages] = useState<string[]|null>(null);
  const isFilterEnabled = props.columns.some(column => column.options?.filter === true);

  const reportAlerts = (messages: string[]) => setAlertMessages(messages);

  const clearAlertMessages = () => setAlertMessages(null);

  const displayAlert = () => (
    <Snackbar open={alertMessages != null} onClose={clearAlertMessages}>
      <Alert severity="warning" onClose={clearAlertMessages}>
        <AlertTitle>Uwaga</AlertTitle>
        { alertMessages !== null && alertMessages.map(message => <div>{message}</div>) }
      </Alert>
    </Snackbar>
  );

  const fetchData = () => {
    console.debug(`Fetching data: ${props.resource}`);
    const headers = [{ name: 'Authorization', value: `Bearer ${keycloak.token }` }];
    Http.get(`/api/${props.resource}`, headers)
      .then(data => {
        setSelectedIndexes([]);
        setData((data as []).map(item => props.convert ? props.convert(item) : item));
      })
      .catch(reason => console.error(`Could not fetch data: ${reason}`));
  };

  const getSelectedId = (): number | null => data ? (data[selectedIndexes[0]] as any).id : null;

  const getSelectedItem = (): any => data ? data[selectedIndexes[0]] : null;

  const getSelectedIds = (): number[] => data ? selectedIndexes.map(index => (data[index] as any).id) : [];

  const closeDetailsDialog = () => {
    setDisplayDetails(false);
    setDisplayedId(null);
  };

  const afterDetailsSaved = (responseData: any, closeDialog: boolean) => {
    if (closeDialog) {
      closeDetailsDialog();
    }
    fetchData();
    props.afterSave && props.afterSave(responseData, closeDialog);
  };

  const handleAdd = () => setDisplayDetails(true);

  const handleEdit = () => {
    const selectedId = getSelectedId();
    console.debug(`Editing id: ${selectedId}`);
    setDisplayDetails(true);
    setDisplayedId(selectedId);
    const selectedItem = getSelectedItem();
    console.debug(`Selected item: ${JSON.stringify(selectedItem)}`);
    setDisplayedItem(selectedItem);
  };

  const handleDelete = () => {
    const selectedId = getSelectedId();
    console.debug(`Deleting id: ${selectedId}`);
    deleteResource(selectedId)
      .then(data => {
        console.debug(`Deleted: ${JSON.stringify(data)}`);
        fetchData();
      })
      .catch(reason => {
        console.error(`Could not delete: ${reason}`);
        reportAlerts(['Wystąpił błąd podczas usuwania wpisu. Proszę sprawdzić, czy nie jest używany on przez inne obiekty']);
      });
  };

  const deleteResource = (resourceId: number | null) => {
    const headers = [{ name: 'Authorization', value: `Bearer ${keycloak.token }` }];
    return props.onDelete === undefined
      ? Http.delete(`/api/${props.resource}/${resourceId}`, headers)
      : props.onDelete(resourceId);
  };

  const handleRowSelectionChange = (currentRowsSelected: any[], allRowsSelected: any[]) => {
    console.debug(`Row selection change, currentRowsSelected: ${JSON.stringify(currentRowsSelected)}, allRowsSelected: ${JSON.stringify(allRowsSelected)}`);
    setSelectedIndexes(allRowsSelected.map(selection => selection.dataIndex) || []);
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.ctrlKey) {
      if (event.key === '+' && isCreateEnabled) {
        handleAdd();
      } else if (event.key === 'Delete' && deleteEnabled) {
        handleDelete();
      } else if (event.key === 'Enter' && editEnabled) {
        handleEdit();
      }
    }
  };

  useEffect(() => {
      if (keycloak.authenticated === true) {
        fetchData();
      }
    }, [props.resource, props.convert, props.lastViewRefresh, keycloak.authenticated]
  );

  return (
    <main onKeyUp={handleKeyUp}>
      { alertMessages && displayAlert() }
      <ButtonsPanel
        editEnabled={editEnabled}
        deleteEnabled={deleteEnabled}
        createEnabled={isCreateEnabled}
        deleteRequiresConfirmation={props.deleteRequiresConfirmation}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        renderCustomButtons={classes => props.renderCustomButtons ? props.renderCustomButtons(classes, getSelectedIds, fetchData, reportAlerts) : <></>}
      />
      <MUIDataTable
        columns={props.columns}
        data={data || []}
        options={({
          customSort: props.sortData,
          sortOrder: props.defaultSortOrder,
          download: false,
          filter: isFilterEnabled,
          filterType: 'checkbox',
          onRowSelectionChange: handleRowSelectionChange,
          print: false,
          rowsSelected: selectedIndexes,
          search: props.tableSearchEnabled || false,
          selectableRows: props.selectableRows || 'single',
          selectableRowsOnClick: true,
          selectToolbarPlacement: 'none',          
          textLabels: {
            body: {
              noMatch: data ? 'Brak danych' : <CircularProgress />
            },
            filter: {
              title: 'Filtruj wpisy',
              reset: 'Wyczyść filtr'
            },
            pagination: {
              displayRows: 'z',
              jumpToPage: 'Przejdź do strony',
              next: 'Następna strona',
              previous: 'Poprzednia strona',
              rowsPerPage: 'Wpisów na stronie'
            },
          },
          viewColumns: false,
        })}
        title={props.title}
      />
      { displayDetails &&
        props.renderDetails({
          instanceId: displayedId,
          afterSave: afterDetailsSaved,
          onCancel: closeDetailsDialog,
          reportAlert: (message: string) => reportAlerts([message]),
          reportAlerts: reportAlerts,
        }, displayedItem)
      }
    </main>
  );
};
export default DataPanel;